import { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { SpecsEditorApi } from "./useSpecsEditor";
import type { SpecsLayer } from "./types";
import { parseSpecsNavHash, serializeSpecsNavHash } from "./specsNavHash";

interface UseSpecsEditorHashNavigationArgs {
  editor: SpecsEditorApi;
}

function findScopedLayer(
  layers: SpecsLayer[],
  partName: string,
  elementName: string,
  styleName: string,
  layerId?: string
) {
  const scopedLayers = layers.filter((item) => {
    return (
      item.modelLayerPath?.partName === partName &&
      item.modelLayerPath?.elementName === elementName &&
      item.modelLayerPath?.styleName === styleName
    );
  });

  if (layerId) {
    return scopedLayers.find((item) => item.id === layerId) ?? null;
  }

  return scopedLayers[0] ?? null;
}

export function useSpecsEditorHashNavigation({
  editor,
}: UseSpecsEditorHashNavigationArgs) {
  const navigate = useNavigate();
  const location = useLocation();
  const initialHashHandledRef = useRef(false);
  const initialHashRef = useRef(location.hash);
  const {
    importSelectedProduct,
    importSelectedVersion,
    selectedNav,
    allLayers,
    currentVersion,
    identity,
    selectSettings,
    selectImport,
    selectHistory,
    selectFamilies,
    selectFabrics,
    selectDecorations,
    selectPositions,
    selectParts,
    selectElements,
    selectStyles,
    selectShipping,
    selectMetadata,
    selectGlobal,
    selectElement,
    selectLayer,
    selectDesign,
  } = editor;

  const parsedInitialHashNav = useMemo(
    () => parseSpecsNavHash(initialHashRef.current),
    []
  );
  const currentNavHash = useMemo(
    () => serializeSpecsNavHash(selectedNav),
    [selectedNav]
  );

  useEffect(() => {
    if (initialHashHandledRef.current) return;

    const waitingForImportedModel =
      !!importSelectedProduct && (!importSelectedVersion || !currentVersion);

    if (waitingForImportedModel) return;

    if (!parsedInitialHashNav) {
      initialHashHandledRef.current = true;
      if (importSelectedProduct && importSelectedVersion) {
        selectDesign();
      }
      return;
    }

    switch (parsedInitialHashNav.kind) {
      case "settings":
        initialHashHandledRef.current = true;
        selectSettings();
        return;
      case "import":
        initialHashHandledRef.current = true;
        selectImport();
        return;
      case "history":
        initialHashHandledRef.current = true;
        selectHistory();
        return;
      case "families":
        initialHashHandledRef.current = true;
        selectFamilies(parsedInitialHashNav.specName);
        return;
      case "fabrics":
        initialHashHandledRef.current = true;
        selectFabrics(parsedInitialHashNav.specName);
        return;
      case "decorations":
        initialHashHandledRef.current = true;
        selectDecorations(parsedInitialHashNav.specName);
        return;
      case "familiesHistory":
        initialHashHandledRef.current = true;
        editor.selectFamiliesHistory(parsedInitialHashNav.specName);
        return;
      case "fabricsHistory":
        initialHashHandledRef.current = true;
        editor.selectFabricsHistory(parsedInitialHashNav.specName);
        return;
      case "decorationsHistory":
        initialHashHandledRef.current = true;
        editor.selectDecorationsHistory(parsedInitialHashNav.specName);
        return;
      case "metadata":
        initialHashHandledRef.current = true;
        selectMetadata();
        return;
      case "global":
        initialHashHandledRef.current = true;
        selectGlobal();
        return;
      case "positions":
        initialHashHandledRef.current = true;
        selectPositions({
          positionId: parsedInitialHashNav.positionId,
          partName: parsedInitialHashNav.part,
          elementName: parsedInitialHashNav.element,
          styleName: parsedInitialHashNav.style,
          layerId: parsedInitialHashNav.layerId,
        });
        return;
      case "parts":
        initialHashHandledRef.current = true;
        selectParts(parsedInitialHashNav.part);
        return;
      case "elements":
        initialHashHandledRef.current = true;
        selectElements();
        return;
      case "styles":
        initialHashHandledRef.current = true;
        selectStyles();
        return;
      case "layerSpecs":
        initialHashHandledRef.current = true;
        editor.setNav({
          kind: "layerSpecs",
          product: identity.productName,
          version: identity.productVersion,
          part: parsedInitialHashNav.part,
          element: parsedInitialHashNav.element,
          style: parsedInitialHashNav.style,
          layerName: parsedInitialHashNav.layerName,
        });
        return;
      case "shipping":
        initialHashHandledRef.current = true;
        selectShipping();
        return;
      case "element": {
        const element = currentVersion?.getElement(
          parsedInitialHashNav.element,
          parsedInitialHashNav.part
        );
        initialHashHandledRef.current = true;
        if (!element) return;
        selectElement(
          parsedInitialHashNav.part,
          parsedInitialHashNav.element,
          parsedInitialHashNav.style
        );
        return;
      }
      case "layer": {
        const layer = findScopedLayer(
          allLayers,
          parsedInitialHashNav.part,
          parsedInitialHashNav.element,
          parsedInitialHashNav.style,
          parsedInitialHashNav.layerId
        );
        if (layer) {
          initialHashHandledRef.current = true;
          selectLayer(layer.id);
          return;
        }

        const element = currentVersion?.getElement(
          parsedInitialHashNav.element,
          parsedInitialHashNav.part
        );
        initialHashHandledRef.current = true;
        if (!element) return;
        selectElement(
          parsedInitialHashNav.part,
          parsedInitialHashNav.element,
          parsedInitialHashNav.style
        );
      }
    }
  }, [
    importSelectedProduct,
    importSelectedVersion,
    parsedInitialHashNav,
    selectElement,
    selectFamilies,
    selectFabrics,
    selectDecorations,
    selectGlobal,
    selectHistory,
    selectLayer,
    selectMetadata,
    selectParts,
    selectElements,
    selectStyles,
    selectShipping,
    selectPositions,
    selectImport,
    selectSettings,
    selectDesign,
    currentVersion,
    allLayers,
    identity.productName,
    identity.productVersion,
  ]);

  useEffect(() => {
    if (!initialHashHandledRef.current && initialHashRef.current) return;

    const nextPath =
      importSelectedProduct && importSelectedVersion
        ? `/specs/${importSelectedProduct}/${importSelectedVersion}`
        : "/specs";

    if (window.location.pathname !== nextPath) {
      navigate(`${nextPath}${currentNavHash}`, { replace: true });
      return;
    }

    if (window.location.hash !== currentNavHash) {
      window.history.replaceState(
        window.history.state,
        "",
        `${nextPath}${window.location.search}${currentNavHash}`
      );
    }
  }, [currentNavHash, importSelectedProduct, importSelectedVersion, navigate]); // eslint-disable-line react-hooks/exhaustive-deps
}
